import { Link } from 'react-router-dom';
import { Heart, Mail, MapPin, Phone, Instagram, Facebook, MessageCircle, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from '../context/ThemeContext';

const Footer = () => {
  const { cursorEffect, toggleCursorEffect } = useTheme();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'About Us', href: '/about' },
    { name: 'Programs', href: '/programs' },
    { name: 'Upcoming Events', href: '/events' },
    { name: 'Jummah Prayer', href: '/jummah' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'News & Updates', href: '/news' },
  ];

  const supportLinks = [
    { name: 'Donate', href: '/donate' },
    { name: 'Volunteer', href: '/volunteer' },
    { name: 'Request Support', href: '/support' },
    { name: 'Contact Us', href: '/contact' },
  ];

  return (
    <footer className="bg-card border-t border-border islamic-pattern">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-3" data-testid="footer-logo">
              <img
                src="/logo.png"
                alt="True North Muslim Community"
                className="h-16 w-16 rounded-xl object-contain bg-black p-1 ring-1 ring-secondary/40 shadow-md"
              />
              <span className="font-semibold text-foreground font-['Outfit']">True North Muslim</span>
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Strengthening faith. Serving community. Supporting orphans.
            </p>
            <div className="flex items-center gap-2">
              <a
                href="https://instagram.com/true.north.muslim"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-accent hover:bg-primary/20 transition-colors"
                data-testid="footer-instagram"
                aria-label="Instagram"
              >
                <Instagram className="h-4 w-4 text-primary" />
              </a>
              <a
                href="https://facebook.com/TrueNorthMuslimCommunity"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-accent hover:bg-primary/20 transition-colors"
                data-testid="footer-facebook"
                aria-label="Facebook"
              >
                <Facebook className="h-4 w-4 text-primary" />
              </a>
              <a
                href="https://chat.whatsapp.com/BZpW0tw4MUuLAVuZmzBRDU?mode=gi_t"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-accent hover:bg-green-500/20 transition-colors"
                data-testid="footer-whatsapp"
                aria-label="WhatsApp"
              >
                <MessageCircle className="h-4 w-4 text-green-500" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-foreground font-['Outfit'] mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-muted-foreground hover:text-primary transition-colors text-sm"
                    data-testid={`footer-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-semibold text-foreground font-['Outfit'] mb-4">Support</h4>
            <ul className="space-y-2">
              {supportLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-muted-foreground hover:text-primary transition-colors text-sm"
                    data-testid={`footer-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-foreground font-['Outfit'] mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-sm">
                <MapPin className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">
                  54 St. Paul Crescent<br />
                  Barrie, ON L9J 0M2
                </span>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-primary flex-shrink-0" />
                <a href="mailto:support@truenorthmuslimcommunity.ca" className="text-muted-foreground hover:text-primary transition-colors">
                  support@truenorthmuslimcommunity.ca
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-muted-foreground text-sm">
            © {currentYear} True North Muslim Community. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleCursorEffect}
              className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors"
              data-testid="footer-cursor-toggle"
            >
              <Sparkles className={`h-3 w-3 ${cursorEffect ? 'text-primary' : ''}`} />
              Cursor Effect: {cursorEffect ? 'On' : 'Off'}
            </button>
            <Link to="/donate" data-testid="footer-donate-btn">
              <Button size="sm" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Heart className="h-3 w-3 mr-1" />
                Support Us
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
